import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUploader from "@/components/admin/ImageUploader";
import { ExportButtons } from "@/lib/exportData";
import { isLegacyStoreContent } from "@/lib/xplosivContent";

const empty = { title: "", video_url: "", poster: "", product_slug: "", is_published: true };

export default function AdminReels() {
    const [items, setItems] = useState([]);
    const [products, setProducts] = useState([]);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(null);

    const load = async () => setItems((await api.get("/admin/reels")).data.filter((item) => !isLegacyStoreContent(item)));
    useEffect(() => {
        load();
        api.get("/admin/products").then(r => setProducts(r.data.filter((product) => !product.legacy_store_content && !product.demo_catalog)));
    }, []);

    const save = async (e) => {
        e.preventDefault();
        const payload = { ...form };
        const p = products.find(p => p.slug === form.product_slug);
        payload.product_id = p?.id || null;
        try {
            if (editing) await api.put(`/admin/reels/${editing}`, payload);
            else await api.post("/admin/reels", payload);
            toast.success("Saved"); setEditing(null); setForm(empty); load();
        } catch (e) { toast.error("Save failed"); }
    };
    const del = async (id) => { if (!window.confirm("Delete reel?")) return; await api.delete(`/admin/reels/${id}`); load(); };

    return (
        <div data-testid="admin-reels-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="font-display text-4xl">Event films</h1>
                    <p className="text-muted-foreground mt-1">Publish occasion highlights and production recaps.</p>
                </div>
                <ExportButtons rows={items} filename="xplosiv_event_films" columns={["title", "video_url", "product_slug", "likes", "is_published"]} />
            </div>
            <form onSubmit={save} className="mt-6">
                <Card className="rounded-[20px] border-[#E7DED2] bg-white/80">
                    <CardContent className="p-6 grid sm:grid-cols-2 gap-3">
                        <Fld label="Title"><Input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></Fld>
                        <Fld label="Linked package or product">
                            <Select value={form.product_slug || ""} onValueChange={v => setForm({ ...form, product_slug: v })}>
                                <SelectTrigger className="rounded-2xl"><SelectValue placeholder="Pick an event package" /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                    {products.map(p => <SelectItem key={p.id} value={p.slug}>{p.title}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Fld>
                        <div className="sm:col-span-2">
                            <Label className="font-mono-ui mb-1.5 block">Video file (MP4/WebM up to 80 MB)</Label>
                            <ImageUploader value={form.video_url} onChange={(url) => setForm({ ...form, video_url: url })} accept="video/*" kind="video" maxMB={80} />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="font-mono-ui mb-1.5 block">Poster image (thumbnail)</Label>
                            <ImageUploader value={form.poster} onChange={(url) => setForm({ ...form, poster: url })} />
                        </div>
                        <div className="flex items-center justify-between sm:col-span-2"><Label>Published</Label><Switch checked={form.is_published} onCheckedChange={v => setForm({ ...form, is_published: v })} /></div>
                        <div className="sm:col-span-2 flex gap-3">
                            <button type="submit" className="dw-btn-primary px-5 py-2 font-mono-ui" data-testid="admin-reel-save">{editing ? "Update" : "Add reel"}</button>
                            {editing && <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="dw-btn-secondary px-5 py-2 font-mono-ui">Cancel</button>}
                        </div>
                    </CardContent>
                </Card>
            </form>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {items.map(r => (
                    <Card key={r.id} className="rounded-[20px] border-[#E7DED2] bg-white/80 overflow-hidden">
                        <video src={r.video_url} poster={r.poster} muted className="aspect-[9/16] w-full object-cover" />
                        <CardContent className="p-3">
                            <p className="text-xs truncate">{r.title}</p>
                            <p className="text-[10px] text-muted-foreground">{r.likes} likes</p>
                            <div className="flex gap-2 mt-2">
                                <button onClick={() => { setEditing(r.id); setForm({ title: r.title, video_url: r.video_url, poster: r.poster, product_slug: r.product_slug, is_published: r.is_published }); }} className="text-xs font-mono-ui underline"><Pencil className="h-3 w-3 inline" /></button>
                                <button onClick={() => del(r.id)} className="text-xs font-mono-ui text-[#B42318]"><Trash2 className="h-3 w-3 inline" /></button>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}

function Fld({ label, children }) { return <div><Label className="font-mono-ui mb-1.5 block">{label}</Label>{children}</div>; }
